import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { AccountActions } from './account.actions';
import { of, withLatestFrom } from 'rxjs';
import { AccountAddressValues, AccountPhoneValues } from './account.model';
import { getMembershipNumber } from '@aaa/emember/store-membership';
import { ExecuteService } from '../services/execute.service';
import { ClubApp } from '@aaa/emember/types';
import { RequestError, RequestErrorType } from '../generic-errors';
import { filterByClubIds } from '../utils/filter-by-club-ids';
import {
  MembershipCampanaChangeAddressEvent,
  MembershipCampanaChangeContactEvent,
  MembershipCampanaChangeResponse,
  MembershipCampanaMethod,
} from '@aaa/interface-joinRenew-membership-membershipCampana';
import { CampanaType } from '../campana.type';

@Injectable({ providedIn: 'root' })
export class AccountCampanaSystemEffects {
  store = inject(Store);
  actions$ = inject(Actions).pipe(filterByClubIds([ClubApp.Northway]));
  executeService = inject(ExecuteService);

  updateAddress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updateAddress),
      withLatestFrom(this.store.select(getMembershipNumber)),
      switchMap(([{ data }, memberNumber]) =>
        this.saveAddress(data, memberNumber).pipe(
          map(() => AccountActions.updateAddressSucceeded({})),
          catchError((error) => of(AccountActions.updateAddressFailed({ error }))),
        ),
      ),
    ),
  );

  updatePhone$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AccountActions.updatePhone),
      withLatestFrom(this.store.select(getMembershipNumber)),
      switchMap(([{ data }, memberNumber]) =>
        this.savePhone(data, memberNumber).pipe(
          map(() => AccountActions.updatePhoneSucceeded({})),
          catchError((error) => of(AccountActions.updatePhoneFailed({ error }))),
        ),
      ),
    ),
  );

  saveAddress(data: Partial<AccountAddressValues>, memberNumber: string | null) {
    const payload: MembershipCampanaChangeAddressEvent = {
      method: MembershipCampanaMethod.CHANGE_ADDRESS,
      memberNumber: memberNumber || '',
      address: {
        postCode: data.zipcode || '',
        region: data.state || '',
        street: [data.address1 || '', data.address2 || ''].filter(Boolean),
        locality: data.city || '',
      },
    };

    return this.executeService
      .membershipQuery<CampanaType.ResponseObject<MembershipCampanaChangeResponse>>(payload)
      .pipe(
        map((validateObject) => {
          //Todo: need to extend the error type for multiple cases
          if (validateObject.error) {
            throw new RequestError(RequestErrorType.UpdateAddress, validateObject.error.message);
          }

          return validateObject?.error;
        }),
      );
  }

  savePhone(data: AccountPhoneValues, memberNumber: string | null) {
    const payload: MembershipCampanaChangeContactEvent = {
      method: MembershipCampanaMethod.CHANGE_CONTACT,
      memberNumber: memberNumber || '',
      phone: data.cell,
    };

    return this.executeService
      .membershipQuery<CampanaType.ResponseObject<MembershipCampanaChangeResponse>>(payload)
      .pipe(
        map((validateObject) => {
          //Todo: need to extend the error type for multiple cases
          if (validateObject.meta?.isError) {
            throw new RequestError(RequestErrorType.UpdatePhone, validateObject);
          }

          return validateObject.response;
        }),
      );
  }
}
